import React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { Chip, Stack, Typography } from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import type { SolutionType } from '@app/src/types/resourceExplorer'
import type { OrganizationAvailability } from './availabilityHelpers'
import {
  getSolutionTypeLabel,
  getSortedSolutionTypes,
  getSortedOrganizations,
  isAvailableToAll,
} from './availabilityHelpers'

interface AvailabilityStatusDisplayProps {
  solutionAvailability?: SolutionType[]
  organizationAvailability?: OrganizationAvailability[]
  emptyMessage?: string
}

/**
 * Reusable component for displaying template availability status
 */
const AvailabilityStatusDisplay: React.FC<AvailabilityStatusDisplayProps> = ({
  solutionAvailability,
  organizationAvailability,
  emptyMessage,
}) => {
  const { formatMessage } = useIntl()

  const solutionIcon = <CategoryOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
  const orgIcon = <BusinessOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />

  // Show empty message if provided and no availability data
  if (emptyMessage && !solutionAvailability && !organizationAvailability) {
    return (
      <Stack direction="row" alignItems="center" gap={1} mt={1}>
        <Typography variant="caption" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Stack>
    )
  }

  const bothAvailableToAll = isAvailableToAll(solutionAvailability, organizationAvailability)
  const hasSolutionRestrictions = Boolean(solutionAvailability && solutionAvailability.length > 0)
  const hasOrgRestrictions = Boolean(organizationAvailability && organizationAvailability.length > 0)

  const sortedSolutionTypes = hasSolutionRestrictions
    ? getSortedSolutionTypes(solutionAvailability ?? [], formatMessage)
    : []
  const sortedOrganizations = hasOrgRestrictions
    ? getSortedOrganizations(
        (organizationAvailability ?? []).map(org => ({
          ...org,
          name: org.organization?.name ?? `Org #${org.organizationId}`,
        })),
      )
    : []

  const solutionChips = sortedSolutionTypes.map(type => (
    <Chip key={type} label={getSolutionTypeLabel(type, formatMessage)} size="small" variant="filled" />
  ))

  const orgChips = sortedOrganizations.map(orgAvail => (
    <Link
      key={orgAvail.id}
      to={`/configurations/organizations/${orgAvail.organizationId}`}
      style={{ textDecoration: 'none' }}
    >
      <Chip
        label={orgAvail.name}
        size="small"
        variant="filled"
        sx={{
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      />
    </Link>
  ))

  const renderAvailabilityRow = (
    icon: React.ReactNode,
    hasRestrictions: boolean,
    allAvailableKey: string,
    restrictedKey: string,
    chips: React.ReactNode[],
  ) => (
    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
      {icon}
      <Typography variant="caption" color="text.secondary">
        {formatMessage({ id: hasRestrictions ? restrictedKey : allAvailableKey })}
      </Typography>
      {chips}
    </Stack>
  )

  // Both available to all: show on same row
  if (bothAvailableToAll) {
    return (
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} mt={1}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          {solutionIcon}
          <Typography variant="caption" color="text.secondary">
            {formatMessage({ id: 'configurations.templateConfig.availability.availableForAllSolutionTypes' })}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          •
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          {orgIcon}
          <Typography variant="caption" color="text.secondary">
            {formatMessage({ id: 'configurations.templateConfig.availability.availableForAllOrganizations' })}
          </Typography>
        </Stack>
      </Stack>
    )
  }

  // Has restrictions: show on separate rows
  return (
    <Stack spacing={1} mt={1}>
      {renderAvailabilityRow(
        solutionIcon,
        hasSolutionRestrictions,
        'configurations.templateConfig.availability.availableForAllSolutionTypes',
        'configurations.templateConfig.availability.restrictedToSolutionTypes',
        solutionChips,
      )}
      {renderAvailabilityRow(
        orgIcon,
        hasOrgRestrictions,
        'configurations.templateConfig.availability.availableForAllOrganizations',
        'configurations.templateConfig.availability.restrictedToOrganizations',
        orgChips,
      )}
    </Stack>
  )
}

export default AvailabilityStatusDisplay
